import { ChangeDetectorRef, Component } from '@angular/core';

import { ParticipantPriceType } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import {
    HorseRaceSortingDirection,
    TimeformParticipantWithEventOption,
    TimeformParticipantWithParticipantOption,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';

import { CrmPromotionProviderService } from '../../../crm-promotion/crm-promotion-provider.service';
import { HorsesFixtureHelper } from '../../../horse-racing-shared/services/horses-fixture-helper.service';
import { HorsesFixtureMarketHelper } from '../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { GreyhoundTrackingService } from '../../common/greyhound-tracking.service';
import { HorsesRaceService } from '../../services/horses-race.service';
import { BaseRaceWinMarketService } from '../base/base-race-win-market.service';
import { HorsesRaceWinMarketComponent } from './horses-race-win-market.component';

@Component({
    selector: 'ms-horses-derived-race-win-market',
    templateUrl: 'horses-race-win-market.component.html',
    standalone: false,
})
export class HorsesDerivedRaceWinMarketComponent extends HorsesRaceWinMarketComponent {
    constructor(
        public override sitecore: Sitecore,
        protected override horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        protected override horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected override horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected override horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        protected override crmPromotionProvider: CrmPromotionProviderService,
        protected override changeDetector: ChangeDetectorRef,
        protected override horsesRaceService: HorsesRaceService,
        protected override horsesFixtureHelper: HorsesFixtureHelper,
        protected override greyhoundTrackingService: GreyhoundTrackingService,
        protected override baseRaceWinMarketService: BaseRaceWinMarketService,
    ) {
        super(
            sitecore,
            horsesFixtureSortingHelper,
            horsesFixtureParticipantHelper,
            horsesFixturePricingHelper,
            horsesFixtureMarketHelper,
            horsesRaceService,
            crmPromotionProvider,
            changeDetector,
            horsesFixtureHelper,
            greyhoundTrackingService,
            baseRaceWinMarketService,
        );
    }

    override setViewModel(isNewFixture: boolean): void {
        this.isOptionBet = this.fixture.isOptionBet;
        const runners = this.horsesFixtureParticipantHelper.getParticipants(this.fixture.participants);
        const options = this.horsesFixturePricingHelper.getOptionPrices(this.fixture, this.optionGroup, ParticipantPriceType.Fixed);
        const someOptionsPriced = this.horsesFixturePricingHelper.someOptionsPriced(runners, this.participantOptionGroups);

        let participants;

        if (isNewFixture) {
            // use default sorting
            this.vm.sortBy = someOptionsPriced ? HorseRaceSortingDirection.Odds : HorseRaceSortingDirection.CardNumber;
            participants = this.horsesFixtureSortingHelper.sortV3ParticipantsBy(runners, options, this.vm.sortBy);
            this.isAllexpanded = false;
            this.vm.expandedParticipant = {};
            this.activeOption = this.baseRaceWinMarketService.getActiveSortOption(this.vm.sortBy);
        } else {
            // restore sorting as it was before.
            const sorted = this.horsesRaceService.restoreParticipantOrder(this.vm.runners, this.fixture);
            participants = this.horsesFixtureParticipantHelper.getParticipants(sorted);
        }
        this.vm.runners = this.buildRunners(participants);
        this.vm.nonRunners = this.horsesFixtureParticipantHelper.getCombinedNonRunners(this.fixture);
        this.vm.hasSilks = this.vm.runners.some((_) => !!_.attributes.silkUrl);
        if (this.activeOption.id === HorseRaceSortingDirection.Odds) this.sortParticipantsBy(HorseRaceSortingDirection.Odds);
    }

    override buildRunners(participants: WinGameParticipant[]): (TimeformParticipantWithEventOption | TimeformParticipantWithParticipantOption)[] {
        return participants
            .map((p) => {
                let option: any;
                if (this.displayType === OptionGroupType.Wincast) {
                    const priceType = this.optionGroup.isWinPriceAvailable ? ParticipantPriceType.Fixed : ParticipantPriceType.StartingPrice;
                    option = this.horsesFixturePricingHelper.getParticipantOptions(p.id, this.participantOptionGroups, priceType);
                } else {
                    const partOption = this.participantOptionGroups[p.id];
                    if (partOption) option = partOption[0];
                }

                return {
                    ...p,
                    attributes: this.horsesFixtureParticipantHelper.getHorseRaceInfo(p),
                    option: option,
                    priceHistory: this.horsesFixturePricingHelper.getV3PriceHistory(option),
                    isOptionActive: false,
                } as TimeformParticipantWithEventOption;
            })
            .filter((t) => t.option);
    }

    override sortParticipantsBy(sortBy: HorseRaceSortingDirection): void {
        this.vm.sortBy = sortBy;
        const horses = this.vm.runners.map((runner) => runner);
        const options = this.horsesFixturePricingHelper.getOptionPrices(this.fixture, this.optionGroup, ParticipantPriceType.Fixed);
        const sortedRunners = this.horsesFixtureSortingHelper.sortV3ParticipantsBy(horses, options, sortBy);
        const participants = this.horsesFixtureParticipantHelper.getParticipants(sortedRunners);
        this.vm.runners = this.buildRunners(participants);
    }
}
